/**
 * Trending score — the pure ranking function behind the gallery `sort=trending`
 * order (docs/CREATOR-PLATFORM.md Gallery: "trending = views decay + likes").
 *
 * DESIGN (documented so the ranking can be reasoned about and tuned):
 *   score = (likes * LIKE_WEIGHT + log10(1 + views)) * recencyDecay
 *   recencyDecay = 0.5 ^ (ageHours / HALF_LIFE_HOURS)
 *
 * Rationale:
 *   - Likes are a strong, deliberate signal → weighted heavily and linearly.
 *   - Views are cheap and heavy-tailed → dampened with log10 so a viral view
 *     count can't drown out genuine engagement.
 *   - Recency is a half-life decay on `updatedAt` (a fresh publish/iteration
 *     resurfaces a game) so the gallery stays lively; an untouched game sinks.
 *
 * PURE: no wall-clock read — `now` is passed in (the caller supplies the clock),
 * keeping the function testable and deterministic. All inputs are millis.
 */

/** Weight applied to each like relative to the log-damped view term. */
export const LIKE_WEIGHT = 4;

/** Weight per engaged play — stronger than a raw view, weaker than a like. */
export const PLAY_WEIGHT = 1;

/** Recency half-life: score halves every this-many hours since `updatedAt`. */
export const HALF_LIFE_HOURS = 72;

const MS_PER_HOUR = 3_600_000;

/**
 * Compute a creation's trending score. Higher sorts first.
 *   score = (likes*LIKE_WEIGHT + plays*PLAY_WEIGHT + log10(1+views)) * recencyDecay
 *
 * @param likes     current like count (>= 0)
 * @param views     current view count (>= 0)
 * @param updatedAt last-updated time, millis since epoch
 * @param now       reference time, millis since epoch (caller's clock)
 * @param plays     engaged-play count (>= 0); optional, defaults to 0
 */
export function score(
  likes: number,
  views: number,
  updatedAt: number,
  now: number,
  plays = 0,
): number {
  const safeLikes = Math.max(0, likes);
  const safeViews = Math.max(0, views);
  const safePlays = Math.max(0, plays);
  // A future updatedAt (clock skew) clamps age to 0 rather than boosting.
  const ageHours = Math.max(0, (now - updatedAt) / MS_PER_HOUR);
  const engagement = safeLikes * LIKE_WEIGHT + safePlays * PLAY_WEIGHT + Math.log10(1 + safeViews);
  const recencyDecay = Math.pow(0.5, ageHours / HALF_LIFE_HOURS);
  return engagement * recencyDecay;
}
